import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { camelCase } from "lodash-es";
import { useIntl } from "react-intl";

import { OrganizationSetting } from "@mapmycustomers/shared";
import { EntityType } from "@mapmycustomers/shared/enum";
import User, { UserRef } from "@mapmycustomers/shared/types/User";
import { userDisplayNameComparator } from "@mapmycustomers/shared/util/comparator";
import { SelectFieldProps } from "@mapmycustomers/ui";

import UserSelectField from "@app/component/input/UserSelectField";
import { getOrganizationSettingValue } from "@app/store/iam";
import { getUsers, getUsersOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";

import styles from "./OwnerField.module.scss";

type OwnerFieldProps = {
  disabled?: boolean;
  entityType?: EntityType;
  onChange?: (ownerId?: User["id"]) => void;
  user?: UserRef;
  users?: User[];
  value?: User["id"];
} & Omit<SelectFieldProps, "onChange" | "value">;

const OwnerField = ({
  allowAllUsers,
  allUsers,
  disabled,
  entityType,
  label,
  onChange,
  required,
  user,
  users,
  value,
}: OwnerFieldProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();

  const availableUsers = useMemo(() => {
    const adjustedEntityType = camelCase(entityType ?? ""); // because it is crmActivities in setting value
    const showAllUsers = allowAllUsers?.[adjustedEntityType] ?? false;
    if (showAllUsers) {
      return allUsers;
    }

    if (!value || users.some(({ id }) => id === value)) {
      return users;
    }
    // handling a case for a deleted user
    const userValue = (user as User) ?? allUsers.find(({ id }) => id === value);
    return userValue ? [...users, userValue].sort(userDisplayNameComparator) : users;
  }, [allUsers, user, users, value, entityType, allowAllUsers]);

  const userFilterOption = useCallback(
    (inputValue: string, option: any) => {
      const user = availableUsers.find(({ id }) => id === parseInt(option?.value));
      return ((user?.fullName ?? "") + (user?.username ?? ""))
        .toLowerCase()
        .includes(inputValue.toLowerCase().trim());
    },
    [availableUsers]
  );

  const handleUserSelect = useCallback((userId: User["id"]) => onChange?.(userId), [onChange]);

  return (
    <UserSelectField
      className={styles.select}
      defaultUsersOption={false}
      dropdownMatchSelectWidth={false}
      filterOption={userFilterOption}
      label={label}
      locked={disabled}
      onSelect={handleUserSelect}
      placeholder={intl.formatMessage({
        id: "formFields.ownerField.placeholder",
        defaultMessage: "Type to find user by name or email",
        description: "Placeholder of the Owner field",
      })}
      required={required}
      showSearch
      users={availableUsers}
      value={availableUsers.find(({ id }) => id === value)}
    />
  );
};

const mapStateToProps = (state: RootState, props: OwnerFieldProps) => ({
  allowAllUsers: getOrganizationSettingValue(
    state,
    OrganizationSetting.ALLOW_OWNERSHIP_TO_ALL_USERS
  ),
  allUsers: getUsersOfEntireOrganization(state),
  users: props.users ?? getUsers(state),
});

export default connect(mapStateToProps, {})(OwnerField);
